import { useState } from "react";

import { useNavigate } from "@tanstack/react-router";
import { ArrowRight, LoaderCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useRequestOtp, useVerifyOtp } from "@/hooks/mutations/use-auth-otp";
import { setAccessTokenCookie } from "@/utils/cookies";

import { DEFAULT_PROTECTED_ROUTE } from "@constants/auth";

import { useAuth } from "@hooks/useAuth";

import { isInternalEmail } from "@utils/authRouting";

type Step = "email" | "code";

/**
 * Email-first, auto-branching sign-in:
 *  - internal (staff) email domain -> Microsoft (Azure AD) redirect
 *  - everyone else -> passwordless email one-time-code flow
 */
export const SignInFlow = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const requestOtp = useRequestOtp();
  const verifyOtp = useVerifyOtp();

  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    const trimmed = email.trim();
    if (!trimmed.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }

    // Internal staff -> Microsoft sign-in (unchanged path).
    if (isInternalEmail(trimmed)) {
      login();
      return;
    }

    // External users -> request a one-time code. Response is generic by design.
    try {
      await requestOtp.mutateAsync(trimmed);
      setStep("code");
      setMessage("If your email is approved, an 8-digit code has been sent.");
    } catch {
      setError("Something went wrong. Please try again.");
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const data = await verifyOtp.mutateAsync({
        email: email.trim(),
        code: code.trim(),
      });
      setAccessTokenCookie(data.access_token);
      navigate({ to: DEFAULT_PROTECTED_ROUTE, replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid or expired code.");
    }
  };

  const handleResend = async () => {
    setError(null);
    setMessage(null);
    try {
      await requestOtp.mutateAsync(email.trim());
      setMessage("If your email is approved, a new code has been sent.");
    } catch {
      setError("Something went wrong. Please try again.");
    }
  };

  if (step === "code") {
    return (
      <form onSubmit={handleVerify} className="flex max-w-md flex-col gap-3">
        <p className="text-sm text-dark-100">
          Enter the 8-digit code sent to{" "}
          <span className="font-semibold">{email}</span>.
        </p>
        <Input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={8}
          placeholder="12345678"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          aria-label="One-time code"
        />
        {message && <p className="text-sm text-dark-100">{message}</p>}
        {error && <p className="text-sm text-red-400">{error}</p>}
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={verifyOtp.isPending || code.length !== 8}>
            {verifyOtp.isPending ? (
              <>
                Verifying <LoaderCircle className="ml-2 animate-spin" />
              </>
            ) : (
              <>
                Verify <ArrowRight className="ml-1" />
              </>
            )}
          </Button>
          <Button
            type="button"
            variant="dark-outline"
            onClick={handleResend}
            disabled={requestOtp.isPending}
          >
            Resend code
          </Button>
          <button
            type="button"
            className="text-sm text-dark-100 underline"
            onClick={() => {
              setStep("email");
              setCode("");
              setError(null);
              setMessage(null);
            }}
          >
            Use a different email
          </button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={handleContinue} className="flex max-w-md flex-col gap-3">
      <Input
        type="email"
        autoComplete="email"
        placeholder="you@example.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        aria-label="Email"
      />
      {error && <p className="text-sm text-red-400">{error}</p>}
      <div className="flex items-center gap-4">
        <Button type="submit" className="lg:px-8" disabled={requestOtp.isPending}>
          {requestOtp.isPending ? (
            <>
              Continue <LoaderCircle className="ml-2 animate-spin" />
            </>
          ) : (
            <>
              Continue <ArrowRight className="ml-1" />
            </>
          )}
        </Button>
        <Button type="button" variant="dark-outline" className="lg:px-16">
          Request Demo
        </Button>
      </div>
    </form>
  );
};
